import React, { useRef, useState } from "react";
import { Group, NumberInput, Paper, SegmentedControl, Select, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { trackGoal } from "../../helpers/trackGoal";
import {
  FeeCurrency,
  FeeTier,
  SCRIPT_TYPES,
  ScriptType,
  formatSats,
  rateLabel,
  satsFor,
  satsToMoney,
  txVbytes,
} from "../helpers/feeMath";

const PRESETS: Record<string, { inputs: number; outputs: number }> = {
  simple: { inputs: 1, outputs: 2 },
  many: { inputs: 1, outputs: 10 },
  consolidate: { inputs: 10, outputs: 1 },
};

const PRESET_HINTS: Record<string, string> = {
  simple: "One payment plus change back to your wallet.",
  many: "One transaction paying several people (batching), plus change.",
  consolidate: "Combine many small coins into one, sent to yourself.",
};

const MAX_COUNT = 1000;

interface Props {
  tiers: FeeTier[];
  currency: FeeCurrency;
  panelStyle: React.CSSProperties;
}

const FeeCalculator: React.FC<Props> = ({ tiers, currency, panelStyle }) => {
  const [preset, setPreset] = useState("simple");
  const [scriptType, setScriptType] = useState<ScriptType>("p2wpkh");
  // what's in the boxes (may be "" while typing); the math uses the clamped counts
  const [inputsRaw, setInputs] = useState<string | number>(1);
  const [outputsRaw, setOutputs] = useState<string | number>(2);
  const tracked = useRef(false);

  // one Plausible goal per page view, on the first change
  const used = () => {
    if (tracked.current) return;
    tracked.current = true;
    trackGoal("Fee calculator used");
  };

  const count = (value: string | number) => Math.min(MAX_COUNT, Math.max(1, Math.floor(Number(value) || 1)));
  const inputs = count(inputsRaw);
  const outputs = count(outputsRaw);
  const vbytes = txVbytes(scriptType, inputs, outputs);

  return (
    <Paper p={{ base: "md", sm: "lg" }} radius="md" style={panelStyle}>
      <Stack gap="md">
        <div>
          <Title order={2} c="white" size="h3">
            Bitcoin fee calculator
          </Title>
          <Text size="sm" c="dimmed">
            What a transaction costs at each speed, from its inputs and outputs.
          </Text>
        </div>

        <SegmentedControl
          fullWidth
          value={preset}
          onChange={(value) => {
            used();
            setPreset(value);
            setInputs(PRESETS[value].inputs);
            setOutputs(PRESETS[value].outputs);
          }}
          data={[
            { value: "simple", label: "Simple send" },
            { value: "many", label: "Send to many" },
            { value: "consolidate", label: "Consolidate" },
          ]}
        />
        <Text size="xs" c="dimmed" mt={-8}>
          {PRESET_HINTS[preset]}
        </Text>

        <Select
          label="Wallet address type"
          maw={360}
          value={scriptType}
          onChange={(value) => {
            if (!value) return;
            used();
            setScriptType(value as ScriptType);
          }}
          data={SCRIPT_TYPES.map((s) => ({ value: s.value, label: s.label }))}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
        <SimpleGrid cols={2} spacing="sm">
          <NumberInput
            label="Inputs (coins spent)"
            value={inputsRaw}
            min={1}
            max={MAX_COUNT}
            allowDecimal={false}
            allowNegative={false}
            onChange={(value) => {
              used();
              setInputs(value);
            }}
          />
          <NumberInput
            label="Outputs (incl. change)"
            value={outputsRaw}
            min={1}
            max={MAX_COUNT}
            allowDecimal={false}
            allowNegative={false}
            onChange={(value) => {
              used();
              setOutputs(value);
            }}
          />
        </SimpleGrid>

        <Group gap={6} wrap="wrap">
          <Text size="sm" c="dimmed">
            Size:
          </Text>
          <Text size="sm" c="white" fw={700} style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatSats(vbytes)} vB
          </Text>
        </Group>

        <Table
          verticalSpacing={6}
          horizontalSpacing={4}
          style={{ fontVariantNumeric: "tabular-nums", background: "transparent" }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th c="dimmed" fw={400}>Speed</Table.Th>
              <Table.Th c="dimmed" fw={400} ta="right">sat/vB</Table.Th>
              <Table.Th c="dimmed" fw={400} ta="right">Fee</Table.Th>
              <Table.Th c="dimmed" fw={400} ta="right">{currency.code.toUpperCase()}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tiers.map((tier) => {
              const sats = satsFor(tier.rate, vbytes);
              return (
                <Table.Tr key={tier.key}>
                  <Table.Td c="white">{tier.label}</Table.Td>
                  <Table.Td ta="right">{rateLabel(tier.rate)}</Table.Td>
                  <Table.Td ta="right">{formatSats(sats)} sats</Table.Td>
                  <Table.Td ta="right" c="orange.4" fw={700}>
                    {satsToMoney(sats, currency)}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        <Text size="xs" c="dimmed">
          Sizes assume single-signature wallets and that the outputs use the same address type. Multisig and
          other scripts are bigger.
        </Text>
      </Stack>
    </Paper>
  );
};

export default FeeCalculator;
